import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, LinearProgress, Divider,
  Tab, Tabs, Tooltip, IconButton
} from '@mui/material';
import {
  Analytics, People, School, AttachMoney, EventNote,
  Gavel, Print, FileDownload, Refresh, TrendingUp,
  Warning, CheckCircle, BarChart, PieChart, TableChart
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fmt  = n => `E${parseFloat(n || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`;
const fmtN = n => parseFloat(n || 0).toLocaleString();
const pct  = n => `${parseFloat(n || 0).toFixed(1)}%`;

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Shared Year Selector ───────────────────────────────────────────────────────
function YearSelect({ value, onChange, academicYears }) {
  return (
    <TextField select size="small" label="Academic Year" value={value} onChange={e => onChange(e.target.value)} sx={{ minWidth: 200 }}>
      <MenuItem value="">All years</MenuItem>
      {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
    </TextField>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = '#0f2b4a', icon, alert }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${alert ? '#c62828' : color}` }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{label}</Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: alert ? '#c62828' : color, mt: 0.3 }}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ color: alert ? '#c62828' : color, opacity: 0.25, fontSize: 36 }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Overview / Dashboard Tab ───────────────────────────────────────────────────
function DashboardTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    setLoading(true);
    api.get('/analytics/dashboard', { params: { academic_year_id: selectedYear || undefined } })
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <YearSelect value={selectedYear} onChange={setYear} academicYears={academicYears} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={() => setYear(v => v)}><Refresh /></IconButton></Tooltip>
      </Stack>
      {loading ? <CircularProgress /> : data && (
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Total Enrolled" value={fmtN(data.enrolment?.total)} icon={<People />}
              sub={`${fmtN(data.enrolment?.male)}M / ${fmtN(data.enrolment?.female)}F`} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Active Teachers" value={fmtN(data.teachers)} icon={<School />} color="#2e7d32"
              sub={`${data.enrolment?.total && data.teachers ? Math.round(data.enrolment.total / data.teachers) : '—'}:1 ratio`} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Attendance Rate" value={pct(data.attendance_rate)} icon={<EventNote />}
              color={data.attendance_rate >= 85 ? '#2e7d32' : '#e65100'}
              alert={data.attendance_rate < 70} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Avg Academic Score" value={pct(data.avg_academic_score)} icon={<BarChart />}
              color={data.avg_academic_score >= 60 ? '#2e7d32' : '#e65100'}
              alert={data.avg_academic_score < 50} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Fee Collection Rate" value={pct(data.fee_collection_rate)} icon={<AttachMoney />}
              color="#c9952a" sub={`Outstanding: ${fmt(data.outstanding_fees)}`}
              alert={data.fee_collection_rate < 50} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Open Disciplinary" value={fmtN(data.open_disciplinary)} icon={<Gavel />}
              color={data.open_disciplinary > 10 ? '#c62828' : '#0f2b4a'}
              alert={data.open_disciplinary > 20} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ── Enrolment Tab ──────────────────────────────────────────────────────────────
function EnrolmentTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/analytics/enrolment', { params: { academic_year_id: selectedYear || undefined } })
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <YearSelect value={selectedYear} onChange={setYear} academicYears={academicYears} />
        <Box sx={{ flexGrow: 1 }} />
        {data && <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('enrolment_by_class', ['Class', 'Stream', 'Total', 'Male', 'Female', 'Other'],
            data.by_class.map(c => [c.class_name, c.stream || '', c.total, c.male, c.female, c.other || 0]))
        }>Export</Button>}
      </Stack>
      {loading ? <CircularProgress /> : data && (
        <Grid container spacing={2}>
          {/* Summary */}
          <Grid item xs={6} sm={3}><StatCard label="Total Enrolled" value={fmtN(data.total)} icon={<People />} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Male" value={fmtN(data.by_gender?.find(g => g.gender === 'male')?.count)} color="#1565c0" icon={<People />} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Female" value={fmtN(data.by_gender?.find(g => g.gender === 'female')?.count)} color="#ad1457" icon={<People />} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Gender Ratio" value={`${pct((data.by_gender?.find(g => g.gender === 'male')?.count || 0) / data.total * 100)} M`} color="#555" icon={<PieChart />} /></Grid>

          {/* Year-on-year trend */}
          {data.trend?.length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>📈 Enrolment Trend (Year-on-Year)</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow sx={{ bgcolor: '#0f2b4a' }}>
                        {['Year', 'Term', 'Total', 'Male', 'Female', 'Change'].map(h => <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {data.trend.map((row, i) => {
                          const prev = data.trend[i - 1];
                          const change = prev ? row.total - prev.total : null;
                          return (
                            <TableRow key={i} hover>
                              <TableCell sx={{ fontWeight: 700 }}>{row.year_label}</TableCell>
                              <TableCell>{row.term}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{fmtN(row.total)}</TableCell>
                              <TableCell>{fmtN(row.male)}</TableCell>
                              <TableCell>{fmtN(row.female)}</TableCell>
                              <TableCell>
                                {change !== null && <Chip label={`${change >= 0 ? '+' : ''}${change}`} size="small"
                                  color={change > 0 ? 'success' : change < 0 ? 'error' : 'default'} sx={{ fontSize: 10, fontWeight: 700 }} />}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* By class */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>📚 Enrolment by Class</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow sx={{ bgcolor: '#0f2b4a' }}>
                      {['Class', 'Stream', 'Level', 'Total', 'Male', 'Female', 'Gender Balance'].map(h => <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {data.by_class?.map((row, i) => {
                        const malePct = row.total > 0 ? Math.round((row.male / row.total) * 100) : 50;
                        return (
                          <TableRow key={i} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 700 }}>{row.class_name}</TableCell>
                            <TableCell>{row.stream || '—'}</TableCell>
                            <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{row.level_name || '—'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.total}</TableCell>
                            <TableCell>{row.male}</TableCell>
                            <TableCell>{row.female}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Box sx={{ width: 80, height: 6, bgcolor: '#ad1457', borderRadius: '3px 0 0 3px', overflow: 'hidden' }}>
                                  <Box sx={{ width: `${malePct}%`, height: '100%', bgcolor: '#1565c0' }} />
                                </Box>
                                <Typography fontSize={10} color="text.secondary">{malePct}%M</Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ── Academic Performance Tab ───────────────────────────────────────────────────
function AcademicTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/analytics/academic', { params: { academic_year_id: selectedYear || undefined } })
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const g = data?.grade_dist;
  const gradeTotal = g ? (g.a_grade + g.b_grade + g.c_grade + g.d_grade + g.fail) : 0;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <YearSelect value={selectedYear} onChange={setYear} academicYears={academicYears} />
        <Box sx={{ flexGrow: 1 }} />
        {data && <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('subject_pass_rates', ['Subject', 'Assessed', 'Avg Score', 'Passed', 'Failed', 'Pass Rate'],
            data.by_subject.map(s => [s.subject_name, s.students_assessed, `${s.avg_score}%`, s.passed, s.failed, `${s.students_assessed > 0 ? pct(s.passed / s.students_assessed * 100) : '—'}`]))
        }>Export</Button>}
      </Stack>
      {loading ? <CircularProgress /> : data && (
        <Grid container spacing={2}>
          {/* Grade distribution */}
          {g && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>📊 Grade Distribution (All Assessments)</Typography>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {[
                      { label: 'A (80%+)',  val: g.a_grade, color: '#2e7d32' },
                      { label: 'B (70–79%)', val: g.b_grade, color: '#1565c0' },
                      { label: 'C (60–69%)', val: g.c_grade, color: '#e65100' },
                      { label: 'D (50–59%)', val: g.d_grade, color: '#c9952a' },
                      { label: 'Fail (<50%)', val: g.fail,   color: '#c62828' },
                    ].map(gr => (
                      <Grid item xs key={gr.label}>
                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                          <Typography fontSize={22} fontWeight={800} sx={{ color: gr.color }}>{fmtN(gr.val)}</Typography>
                          <Typography fontSize={10} color="text.secondary">{gr.label}</Typography>
                          <Typography fontSize={10} color="text.secondary">{gradeTotal > 0 ? pct(gr.val / gradeTotal * 100) : '—'}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ height: 10, borderRadius: 5, overflow: 'hidden', display: 'flex' }}>
                    {gradeTotal > 0 && [
                      { val: g.a_grade, color: '#2e7d32' },
                      { val: g.b_grade, color: '#1565c0' },
                      { val: g.c_grade, color: '#e65100' },
                      { val: g.d_grade, color: '#c9952a' },
                      { val: g.fail,   color: '#c62828' },
                    ].map((gr, i) => <Box key={i} sx={{ width: `${(gr.val / gradeTotal) * 100}%`, bgcolor: gr.color }} />)}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* By subject */}
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>📚 Pass Rates by Subject</Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      {['Subject', 'Assessed', 'Avg Score', 'Pass Rate', 'Bar'].map(h => <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {data.by_subject?.map((s, i) => {
                        const passRate = s.students_assessed > 0 ? (s.passed / s.students_assessed * 100) : 0;
                        return (
                          <TableRow key={i} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{s.subject_name}</TableCell>
                            <TableCell align="center">{s.students_assessed}</TableCell>
                            <TableCell><Chip label={`${s.avg_score}%`} size="small" color={s.avg_score >= 60 ? 'success' : s.avg_score >= 50 ? 'warning' : 'error'} sx={{ fontSize: 10, fontWeight: 700 }} /></TableCell>
                            <TableCell sx={{ fontWeight: 700, color: passRate >= 70 ? '#2e7d32' : passRate >= 50 ? '#e65100' : '#c62828' }}>{pct(passRate)}</TableCell>
                            <TableCell sx={{ minWidth: 80 }}>
                              <LinearProgress variant="determinate" value={Math.min(passRate, 100)}
                                color={passRate >= 70 ? 'success' : passRate >= 50 ? 'warning' : 'error'}
                                sx={{ height: 6, borderRadius: 3 }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Top students */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>🏆 Top 10 Students</Typography>
                {data.top_students?.map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.7, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography fontWeight={800} fontSize={16} sx={{ color: i === 0 ? '#c9952a' : i === 1 ? '#aaa' : i === 2 ? '#cd7f32' : '#ccc', minWidth: 24 }}>#{i + 1}</Typography>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography fontSize={12} fontWeight={600} noWrap>{s.first_name} {s.last_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{s.class_name} · {s.subjects} subjects</Typography>
                    </Box>
                    <Chip label={`${s.avg_score}%`} size="small" color="success" sx={{ fontWeight: 700 }} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ── Attendance Tab ─────────────────────────────────────────────────────────────
function AttendanceTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/analytics/attendance', { params: { academic_year_id: selectedYear || undefined } })
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const o = data?.overall;
  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}><YearSelect value={selectedYear} onChange={setYear} academicYears={academicYears} /></Stack>
      {loading ? <CircularProgress /> : data && (
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}><StatCard label="Overall Rate" value={pct(o?.present / o?.total_records * 100)} icon={<CheckCircle />} color={o?.present / o?.total_records > 0.85 ? '#2e7d32' : '#e65100'} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Present" value={fmtN(o?.present)} color="#2e7d32" icon={<People />} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Absent" value={fmtN(o?.absent)} color="#c62828" icon={<Warning />} alert={o?.absent > o?.present * 0.2} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Late / Excused" value={fmtN((o?.late || 0) + (o?.excused || 0))} color="#e65100" icon={<EventNote />} /></Grid>

          {data.chronic_absentees?.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <strong>{data.chronic_absentees.length} student(s) have an absence rate above 20%.</strong> Immediate intervention recommended.
              </Alert>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom color="error">⚠ Chronic Absentees (&gt;20% absence)</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow sx={{ bgcolor: '#0f2b4a' }}>
                        {['Student', 'Class', 'Days Tracked', 'Absent Days', 'Absence Rate'].map(h => <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {data.chronic_absentees.map((s, i) => (
                          <TableRow key={i} hover sx={{ bgcolor: '#fff8f0' }}>
                            <TableCell sx={{ fontWeight: 600 }}>{s.first_name} {s.last_name} <Typography component="span" fontSize={10} color="text.secondary">({s.admission_number})</Typography></TableCell>
                            <TableCell>{s.class_name || '—'}</TableCell>
                            <TableCell>{s.total_days}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'error.main' }}>{s.absent_days}</TableCell>
                            <TableCell><Chip label={pct(s.absence_rate)} size="small" color="error" sx={{ fontWeight: 700 }} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>📊 Attendance Rate by Class</Typography>
                {data.by_class?.map((c, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography fontSize={12} fontWeight={600}>{c.class_name}{c.stream ? ` (${c.stream})` : ''}</Typography>
                      <Typography fontSize={12} fontWeight={700} color={c.attendance_rate >= 85 ? 'success.main' : c.attendance_rate >= 70 ? 'warning.main' : 'error.main'}>
                        {pct(c.attendance_rate)}
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(c.attendance_rate || 0, 100)}
                      color={c.attendance_rate >= 85 ? 'success' : c.attendance_rate >= 70 ? 'warning' : 'error'}
                      sx={{ height: 8, borderRadius: 4 }} />
                    <Typography fontSize={10} color="text.secondary">{fmtN(c.present)} present, {fmtN(c.absent)} absent of {fmtN(c.total)} records</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ── Teachers Tab ───────────────────────────────────────────────────────────────
function TeachersTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/teachers')
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  return (
    <Box>
      {loading ? <CircularProgress /> : data && (
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}><StatCard label="Total Staff" value={fmtN(data.summary?.total)} icon={<School />} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Male" value={fmtN(data.summary?.male)} color="#1565c0" icon={<People />} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Female" value={fmtN(data.summary?.female)} color="#ad1457" icon={<People />} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Student:Teacher" value={`${data.ratio?.ratio}:1`} color="#c9952a" icon={<TrendingUp />} /></Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
                exportCSV('teachers', ['Name', 'Qualification', 'Gender', 'Subjects', 'Classes', 'Status', 'House Master'],
                  data.teachers.map(t => [`${t.first_name} ${t.last_name}`, t.qualification||'', t.gender, t.subjects_taught, t.classes_assigned, t.status, t.house_master_of||'']))
              }>Export</Button>
            </Stack>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    {['Name', 'Qualification', 'Gender', 'Status', 'Subjects', 'Classes', 'House Master'].map(h => <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {data.teachers?.map((t, i) => (
                      <TableRow key={i} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{t.first_name} {t.last_name}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{t.qualification || '—'}</TableCell>
                        <TableCell><Chip label={t.gender} size="small" color={t.gender === 'male' ? 'info' : 'secondary'} sx={{ fontSize: 10 }} /></TableCell>
                        <TableCell><Chip label={t.status} size="small" color={t.status === 'active' ? 'success' : 'default'} sx={{ fontSize: 10 }} /></TableCell>
                        <TableCell align="center">{t.subjects_taught}</TableCell>
                        <TableCell align="center">{t.classes_assigned}</TableCell>
                        <TableCell sx={{ fontSize: 11, color: t.house_master_of ? 'primary.main' : 'text.disabled' }}>{t.house_master_of || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ── Ministry EMIS Tab ──────────────────────────────────────────────────────────
function EmisTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const load = () => {
    if (!selectedYear) return enqueueSnackbar('Select an academic year', { variant: 'warning' });
    setLoading(true);
    api.get('/analytics/emis', { params: { academic_year_id: selectedYear } })
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to generate report', { variant: 'error' }))
      .finally(() => setLoading(false));
  };

  const printReport = () => {
    const token = localStorage.getItem('sms_token');
    window.open(`${API_BASE}/analytics/emis/report?academic_year_id=${selectedYear}&token=${token}`, '_blank');
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        This report produces a <strong>Ministry of Education EMIS statistical return</strong> — containing enrolment by grade and gender, teaching staff counts, pass rates, attendance rate, student movement, boarding, and bursary data. Click "Print EMIS Report" to generate the printable version.
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <YearSelect value={selectedYear} onChange={setYear} academicYears={academicYears} />
        <Button variant="contained" onClick={load} disabled={loading || !selectedYear} sx={{ bgcolor: '#0f2b4a' }}>
          {loading ? <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" /> : null}
          Generate Report
        </Button>
        {data && (
          <Button variant="outlined" startIcon={<Print />} onClick={printReport}>
            Print EMIS Report
          </Button>
        )}
      </Stack>

      {data && (
        <Grid container spacing={2}>
          {/* Quick summary cards */}
          <Grid item xs={6} sm={4} md={2}><StatCard label="Total Enrolled" value={fmtN(data.totals?.total_enrolled)} icon={<People />} /></Grid>
          <Grid item xs={6} sm={4} md={2}><StatCard label="Male" value={fmtN(data.totals?.total_male)} color="#1565c0" icon={<People />} /></Grid>
          <Grid item xs={6} sm={4} md={2}><StatCard label="Female" value={fmtN(data.totals?.total_female)} color="#ad1457" icon={<People />} /></Grid>
          <Grid item xs={6} sm={4} md={2}><StatCard label="Teaching Staff" value={fmtN(data.teacher_summary?.active_teachers)} color="#2e7d32" icon={<School />} /></Grid>
          <Grid item xs={6} sm={4} md={2}><StatCard label="S:T Ratio" value={`${data.student_teacher_ratio}:1`} color="#c9952a" icon={<TrendingUp />} /></Grid>
          <Grid item xs={6} sm={4} md={2}><StatCard label="Attendance Rate" value={pct(data.attendance?.attendance_rate)} color={data.attendance?.attendance_rate >= 85 ? '#2e7d32' : '#e65100'} icon={<EventNote />} /></Grid>

          {/* Enrolment by grade */}
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>Enrolment by Grade</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow sx={{ bgcolor: '#0f2b4a' }}>
                      {['Grade', 'Stream', 'Total', 'Male', 'Female'].map(h => <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {data.enrolment_by_grade?.map((g, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{g.grade}</TableCell>
                          <TableCell>{g.stream || '—'}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{g.total}</TableCell>
                          <TableCell>{g.male}</TableCell>
                          <TableCell>{g.female}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#e8f0fe' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 800 }}>TOTAL</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{fmtN(data.totals?.total_enrolled)}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{fmtN(data.totals?.total_male)}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{fmtN(data.totals?.total_female)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Pass rates & other stats */}
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>Subject Pass Rates</Typography>
                  {data.pass_rates?.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                      <Typography fontSize={12}>{s.subject}</Typography>
                      <Chip label={pct(s.pass_rate)} size="small"
                        color={s.pass_rate >= 70 ? 'success' : s.pass_rate >= 50 ? 'warning' : 'error'}
                        sx={{ fontSize: 10, fontWeight: 700 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>Other Indicators</Typography>
                  {[
                    ['Boarding Students', `${fmtN(data.boarding?.total_boarders)} (${data.boarding?.male_boarders}M / ${data.boarding?.female_boarders}F)`],
                    ['Bursary Recipients', fmtN(data.bursaries?.recipients)],
                    ['Transferred Out', fmtN(data.student_movement?.transferred)],
                    ['Expelled', fmtN(data.student_movement?.expelled)],
                    ['Fee Collection Rate', pct(data.fee_collection?.collection_rate)],
                    ['Fee Outstanding', fmt(data.fee_collection?.total_billed - data.fee_collection?.total_collected)],
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, borderBottom: '1px solid #f5f5f5' }}>
                      <Typography fontSize={12} color="text.secondary">{label}</Typography>
                      <Typography fontSize={12} fontWeight={600}>{value}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AnalyticsReports() {
  const [tab, setTab]          = useState(0);
  const [academicYears, setAY] = useState([]);

  useEffect(() => { settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {}); }, []);

  const tabs = [
    { label: 'Dashboard',   icon: <BarChart fontSize="small" /> },
    { label: 'Enrolment',   icon: <People fontSize="small" /> },
    { label: 'Academic',    icon: <School fontSize="small" /> },
    { label: 'Attendance',  icon: <EventNote fontSize="small" /> },
    { label: 'Staff',       icon: <People fontSize="small" /> },
    { label: 'EMIS Report', icon: <Print fontSize="small" /> },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Analytics color="primary" /> Analytics & Ministry Reporting
        </Typography>
        <Typography variant="body2" color="text.secondary">
          School-wide analytics · Enrolment · Academic performance · Attendance · EMIS returns
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }} variant="scrollable" scrollButtons="auto">
        {tabs.map((t, i) => <Tab key={i} icon={t.icon} label={t.label} iconPosition="start" sx={{ fontSize: 12 }} />)}
      </Tabs>

      <TabPanel value={tab} index={0}><DashboardTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><EnrolmentTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={2}><AcademicTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={3}><AttendanceTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={4}><TeachersTab /></TabPanel>
      <TabPanel value={tab} index={5}><EmisTab academicYears={academicYears} /></TabPanel>
    </Box>
  );
}
